// ─── La edición de un clip del Estudio, fuera del panel ──────────────────────
// ADR-2026-09-27-editar-clip-sigue-con-el-panel-cerrado. "Editar clip" guardaba
// todo (el pedido en curso, las versiones, cuál se ve) en el estado del panel:
// cerrarlo lo borraba, aunque el backend siguiera editando y cobrara. Acá vive una
// SESIÓN por clip, en un almacén de módulo que sobrevive a que el panel se
// desmonte: el panel la lee y la escribe, la timeline la pinta ("Editando clip…",
// "Edición lista", error) y el editor la guarda en el clip (`edicionDeClip`) para
// retomarla después de recargar.
//
// La versión 0 es SIEMPRE el clip tal como está en la timeline; las siguientes,
// los resultados. Nada se aplica solo: el usuario mira y elige Guardar o Deshacer.

import { useSyncExternalStore } from 'react';
import { supabase } from './supabase';
import { esperarEdicionOmni, iniciarEdicionOmni, type OmniEditParams } from './omniEdit';

/** `segundos`: el largo del tramo que se editó. Si el clip se recorta mientras se edita,
 *  Guardar vuelve a ese largo: el archivo editado ES ese tramo, no el nuevo. */
export type VersionDeEdicion = { url: string; path?: string; segundos?: number };

export interface SesionDeEdicion {
  versiones: VersionDeEdicion[];
  /** Índice de la versión que se ve (0 = el clip original). */
  actual: number;
  /** Pedido en curso. `jobId` llega apenas el backend lo acepta. */
  enCurso: { jobId?: string; instruccion: string; desde: number; base: number; segundos?: number } | null;
  error: string | null;
  /** Llegó un resultado con el panel cerrado y todavía no se miró. */
  lista: boolean;
  /** El panel de este clip está abierto ahora. */
  abierta: boolean;
}

/** Lo que se guarda en el clip (y en el proyecto): sin URLs firmadas, que vencen. */
export interface EdicionDeClipGuardada {
  /** Las ediciones (sin el original), por path en `video-project-assets`. */
  versiones: Array<{ path: string; segundos?: number }>;
  actual: number;
  enCurso?: { jobId: string; instruccion: string; desde: number; base: number; segundos?: number } | null;
  error?: string | null;
  lista?: boolean;
}

const sesiones = new Map<string, SesionDeEdicion>();
const oyentes = new Set<() => void>();
let foto: ReadonlyMap<string, SesionDeEdicion> = new Map();

function avisar() {
  foto = new Map(sesiones);
  for (const o of oyentes) o();
}

function suscribir(o: () => void) {
  oyentes.add(o);
  return () => oyentes.delete(o);
}

export function sesionDeEdicion(id: string): SesionDeEdicion | undefined {
  return sesiones.get(id);
}

/** Crea la sesión si no existe, con el clip actual como versión 0. */
export function abrirSesionDeEdicion(id: string, original: VersionDeEdicion): SesionDeEdicion {
  const hay = sesiones.get(id);
  if (hay) return hay;
  const nueva: SesionDeEdicion = {
    versiones: [original],
    actual: 0,
    enCurso: null,
    error: null,
    lista: false,
    abierta: false,
  };
  sesiones.set(id, nueva);
  avisar();
  return nueva;
}

export function actualizarSesionDeEdicion(
  id: string,
  cambio: (s: SesionDeEdicion) => SesionDeEdicion,
): void {
  const s = sesiones.get(id);
  if (!s) return;
  sesiones.set(id, cambio(s));
  avisar();
}

export function borrarSesionDeEdicion(id: string): void {
  if (sesiones.delete(id)) avisar();
}

/** Una sesión sin ediciones ni pedido en curso no tiene nada que conservar. */
export function sesionVacia(s: SesionDeEdicion): boolean {
  return s.versiones.length <= 1 && !s.enCurso && !s.error;
}

export function useSesionDeEdicion(id: string | null | undefined): SesionDeEdicion | undefined {
  return useSyncExternalStore(suscribir, () => (id ? sesiones.get(id) : undefined));
}

export function useSesionesDeEdicion(): ReadonlyMap<string, SesionDeEdicion> {
  return useSyncExternalStore(suscribir, () => foto);
}

/** Espera un trabajo (nuevo o retomado) y deja el resultado como versión nueva. */
export async function esperarEnSesion(id: string, jobId: string, base: number): Promise<void> {
  try {
    const r = await esperarEdicionOmni(jobId);
    actualizarSesionDeEdicion(id, (s) => ({
      ...s,
      versiones: [
        ...s.versiones.slice(0, base + 1),
        { url: r.signedUrl, path: r.path, segundos: s.enCurso?.segundos },
      ],
      actual: base + 1,
      enCurso: null,
      error: null,
      lista: !s.abierta,
    }));
  } catch (e) {
    const mensaje = e instanceof Error ? e.message : 'Ocurrió un error al editar el video.';
    actualizarSesionDeEdicion(id, (s) => ({ ...s, enCurso: null, error: mensaje, lista: false }));
  }
}

/**
 * Dispara una edición sobre la versión que se ve. La promesa no depende del panel:
 * si se cierra, el resultado llega igual a la sesión.
 */
export async function editarEnSesion(id: string, params: OmniEditParams): Promise<void> {
  const s = sesiones.get(id);
  if (!s || s.enCurso) return;
  const base = s.actual;
  actualizarSesionDeEdicion(id, (x) => ({
    ...x,
    enCurso: { instruccion: params.instruction, desde: Date.now(), base, segundos: params.durationSec },
    error: null,
    lista: false,
  }));
  let jobId: string;
  try {
    jobId = await iniciarEdicionOmni(params);
  } catch (e) {
    const mensaje = e instanceof Error ? e.message : 'No se pudo iniciar la edición.';
    actualizarSesionDeEdicion(id, (x) => ({ ...x, enCurso: null, error: mensaje }));
    return;
  }
  actualizarSesionDeEdicion(id, (x) => ({ ...x, enCurso: x.enCurso && { ...x.enCurso, jobId } }));
  await esperarEnSesion(id, jobId, base);
}

/** La sesión como se guarda en el clip. `undefined` = no hay nada que guardar. */
export function edicionParaGuardar(s: SesionDeEdicion | undefined): EdicionDeClipGuardada | undefined {
  if (!s || sesionVacia(s)) return undefined;
  const versiones = s.versiones
    .slice(1)
    .filter((v): v is { url: string; path: string; segundos?: number } => !!v.path);
  return {
    versiones: versiones.map((v) => ({ path: v.path, segundos: v.segundos })),
    actual: Math.min(s.actual, versiones.length),
    // Un pedido sin jobId todavía no existe en el backend: no hay qué retomar.
    enCurso: s.enCurso?.jobId ? { ...s.enCurso, jobId: s.enCurso.jobId } : null,
    error: s.error,
    lista: s.lista,
  };
}

/**
 * Al abrir el proyecto: rearma la sesión guardada en el clip (firma de nuevo las
 * ediciones) y, si había un pedido en curso, vuelve a esperarlo.
 */
export async function retomarEdicionDeClip(
  id: string,
  original: VersionDeEdicion,
  guardada: EdicionDeClipGuardada,
): Promise<void> {
  if (sesiones.has(id)) return;
  // La sesión se arma EN EL ACTO (sin URLs todavía): si el autoguardado corre
  // mientras se firman, tiene que encontrarla, o la borraría del proyecto.
  const versiones: VersionDeEdicion[] = [
    original,
    ...guardada.versiones.map((v) => ({ url: '', path: v.path, segundos: v.segundos })),
  ];
  const base = Math.min(guardada.enCurso?.base ?? 0, versiones.length - 1);
  sesiones.set(id, {
    versiones,
    actual: Math.min(guardada.actual, versiones.length - 1),
    enCurso: guardada.enCurso ?? null,
    error: guardada.error ?? null,
    lista: !!guardada.lista,
    abierta: false,
  });
  avisar();
  if (guardada.enCurso?.jobId) void esperarEnSesion(id, guardada.enCurso.jobId, base);
  const firmadas = await Promise.all(
    guardada.versiones.map(async (v) => {
      const { data } = await supabase.storage.from('video-project-assets').createSignedUrl(v.path, 3600);
      return data?.signedUrl ?? '';
    }),
  );
  actualizarSesionDeEdicion(id, (s) => ({
    ...s,
    versiones: s.versiones.map((v, i) => (i >= 1 && i <= firmadas.length && !v.url ? { ...v, url: firmadas[i - 1] ?? '' } : v)),
  }));
}
